import React, { useState, useMemo } from 'react';
import { Button, Input, Table, Alert, Card, CardBody } from 'reactstrap';
import { AdditionalField } from '../types';

type AdditionalFieldsProps = {
  fields: AdditionalField[];
  onAdd: () => void;
  onRemove: (id: string) => void;
  onUpdate: (id: string, field: keyof AdditionalField, value: any) => void;
  onValidateDuplicate?: (fieldName: string, excludeId?: string) => boolean;
};

const AdditionalFields: React.FC<AdditionalFieldsProps> = ({ fields, onAdd, onRemove, onUpdate }) => {
  const [duplicatedField, setDuplicatedField] = useState<string | null>(null);

  const validFields = useMemo(
    () => fields.filter(field => field.name.trim() !== '-'),
    [fields]
  );

  const validateFieldDuplicate = (fieldName: string, excludeId?: string) => {
    const normalizedName = fieldName.trim().toLowerCase();
    
    if (!normalizedName || normalizedName === '-') {
      return false;
    }

    const isDuplicate = validFields.some(
      field => field.id !== excludeId && 
               field.name.trim().toLowerCase() === normalizedName
    );

    return isDuplicate;
  };

  const handleFieldUpdate = (id: string, field: keyof AdditionalField, value: any) => {
    if (field === 'name') {
      setDuplicatedField(null);

      const isDuplicate = validateFieldDuplicate(value, id);
      
      if (isDuplicate) {
        setDuplicatedField(id);
        return;
      }
    }
    
    onUpdate(id, field, value);
  };

  const handleAddField = () => {

    const hasErrors = validFields.some(field => 
      validateFieldDuplicate(field.name, field.id)
    );
    
    if (hasErrors) {
      return;
    }
    
    onAdd();
  };

  return (
    <Card className="shadow border-0 overflow-hidden rounded-3 mb-4">
      <div className="bg-soft-primary p-3 border-bottom border-light d-flex justify-content-between align-items-center">
        <h5 className="card-title mb-0 text-primary fw-semibold d-flex align-items-center">
          <i className="ri-file-list-3-fill me-2 fs-18"></i>
          Campos Adicionales ({validFields.length})
        </h5>
        <Button color="primary" size="sm" onClick={handleAddField}>
          <i className="ri-add-line me-1"></i> Añadir
        </Button>
      </div>
      <CardBody className="p-0 bg-white">
        <div className="table-responsive">
          <Table className="table-nowrap align-middle mb-0 table-hover">
            <thead className="bg-light text-muted text-uppercase small">
            <tr>
              <th>Nombre</th>
              <th>Descripción</th>
              <th style={{ width: '40px' }}></th>
            </tr>
          </thead>
          <tbody>
            {duplicatedField && (
              <tr>
                <td colSpan={3}>
                  <Alert color="danger" className="mb-0">
                    <i className="ri-error-warning-line me-2"></i>
                    Este campo ya existe. Por favor, use un nombre diferente.
                  </Alert>
                </td>
              </tr>
            )}
            {validFields.map((field, index) => {
              const hasError = duplicatedField === field.id;
              const isDuplicate = validateFieldDuplicate(field.name, field.id);
              
              return (
                <tr key={field.id} className={index % 2 === 0 ? 'bg-white' : 'bg-soft-light bg-opacity-25'}>
                  <td>
                    <Input
                      value={field.name}
                      onChange={(e) => handleFieldUpdate(field.id, 'name', e.target.value)}
                      bsSize="sm"
                      placeholder="Nombre del campo"
                      maxLength={25}
                      readOnly={field.readOnly}
                      disabled={field.readOnly}
                      invalid={hasError || isDuplicate}
                    />
                    {hasError && (
                      <div className="text-danger small mt-1">
                        Campo duplicado
                      </div>
                    )}
                  </td>
                  <td>
                    <Input
                      value={field.value}
                      onChange={(e) => handleFieldUpdate(field.id, 'value', e.target.value)}
                      bsSize="sm"
                      placeholder="Valor"
                      maxLength={270}
                      readOnly={field.readOnly}
                      disabled={field.readOnly}
                    />
                  </td>
                  <td>
                    {!field.readOnly && (
                      <Button 
                        color="soft-danger"
                        size="sm"
                        className="btn-icon shadow-none" 
                        onClick={() => onRemove(field.id)}
                        title="Eliminar campo"
                      >
                        <i className="ri-delete-bin-line"></i>
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
            {validFields.length === 0 && (
              <tr>
                <td colSpan={3} className="text-center py-5">
                  <h5 className="text-muted mb-1">No hay campos adicionales configurados</h5>
                  <p className="text-muted mb-0 small">Haga clic en "Añadir" para agregar nueva información.</p>
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
      </CardBody>
    </Card>
  );
};

export default AdditionalFields;
